import Link from "next/link";

import styles from "@/ui/styles/shared.module.css";

import { cn } from "@/utils";
import { Section } from "@/ui/organism/section";

const missionTags = [
	"Frontend Lead",
	"Creative Technologist",
	"Visual Strategist",
	"Three.js Explorer",
];

const heroStats = [
	{
		label: "Years in orbit",
		value: "10+",
		detail: "Shipping immersive products",
	},
	{
		label: "Launches led",
		value: "45",
		detail: "Web, mobile, experiential & brand",
	},
	{
		label: "Impact lift",
		value: "+38%",
		detail: "Avg conversion increase",
	},
];

const heroSignals = [
	{
		title: "Command Stack",
		body: "Next.js · React · Three.js · Framer Motion · WebGL",
		meta: "Zero-to-one execution",
	},
	{
		title: "Crew Partners",
		body: "Blade Club · CICCC · NYP · CozyCot",
		meta: "Trusted collaborators",
	},
	{
		title: "Now Plotting",
		body: "Space-forward digital experiences & interactive stories",
		meta: "Open for Q1 2026",
	},
];

const Hero = () => {
	return (
		<Section
			id="home"
			label="Home"
			order={1}
			className="relative flex w-full flex-col justify-center"
		>
			<div className={styles.heroShell}>
				<div className={styles.starfield} aria-hidden="true" />
				<div className={styles.nebula} aria-hidden="true" />
				<div className="relative z-10 grid gap-8 md:gap-12 lg:gap-16 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
					<div className="space-y-6 md:space-y-8 lg:space-y-10">
						<div className={styles.missionBadge}>
							<span className="h-2 w-2 rounded-full bg-[#f5e36a] shadow-[0_0_12px_#f5e36a]" />
							<span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
								Currently orbiting Vancouver · PST-8
							</span>
						</div>

						<div className="space-y-4 md:space-y-6">
							<p className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-white/50">
								Mission log 2025.01
							</p>
							<h1
								className={cn(
									styles.headerAnimation,
									"text-balance font-black text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-tight",
								)}
							>
								Design-forward frontend that feels like deep space exploration.
							</h1>
							<p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 lg:leading-[1.4]">
								I craft cinematic web experiences for ambitious brands—blending
								art direction, creative code, and motion systems to turn complex
								product stories into playful missions.
							</p>
						</div>

						<div className="flex flex-wrap gap-2 sm:gap-3">
							{missionTags.map((tag) => (
								<span key={tag} className={styles.pulseChip}>
									{tag}
								</span>
							))}
						</div>

						<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
							<Link href="#works" className={styles.primaryCta}>
								<span>View Missions</span>
								<span className={styles.ctaIcon} aria-hidden="true">
									↗
								</span>
							</Link>
							<Link href="#contact" className={styles.secondaryCta}>
								Open Comms
							</Link>
						</div>

						<div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-3">
							{heroStats.map((stat) => (
								<div key={stat.label} className={styles.statCard}>
									<span className="text-3xl sm:text-4xl font-semibold text-white">
										{stat.value}
									</span>
									<p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/60">
										{stat.label}
									</p>
									<p className="text-xs sm:text-sm text-white/70">{stat.detail}</p>
								</div>
							))}
						</div>
					</div>

					<div className={styles.holoPanel}>
						<div className={styles.holoGrid} aria-hidden="true" />

						<div className="relative z-10 flex h-full flex-col gap-6 md:gap-8">
							<div className="flex items-center justify-between gap-4">
								<div>
									<p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/50">
										Next window
									</p>
									<p className="text-base sm:text-lg font-semibold text-white">
										Launch-ready partner
									</p>
								</div>
								<div className={styles.statusPip}>Calibrated</div>
							</div>

						<div className={styles.orbitShell} aria-hidden="true">
							<div className={styles.orbitCore} />
							<div className={styles.orbitTrail} />
							<img
								src="/international-space-station.svg"
								alt=""
								className={styles.orbitSatellite}
								aria-hidden="true"
							/>
						</div>

							<div className="space-y-3 md:space-y-4">
								{heroSignals.map((signal) => (
									<div key={signal.title} className={styles.signalCard}>
										<div className="flex items-center justify-between gap-3">
											<p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-white/60">
												{signal.meta}
											</p>
											<span className={styles.signalDot} />
										</div>
										<h3 className="text-lg sm:text-xl font-semibold text-white">
											{signal.title}
										</h3>
										<p className="text-sm sm:text-base text-white/80">{signal.body}</p>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Section>
	);
};

export default Hero;
